/* eslint-disable no-shadow */
export enum TimeScale {
    Weeks = 'w',
    Days = 'd',
    Hours = 'h',
    Minutes = 'm',
    Seconds = 's',
    Milliseconds = 'ms',
    Microseconds = 'us',
    Nanoseconds = 'ns',
}

type TimeScaleDetails = {
    unit: string;
    scaleUp: string | null;
    scaleUpValue: number | null;
    decimalPlaces: number;
};

/**
 * Mapping of scale key to details that govern scaling and visual display.
 */
const timeScaleMap: { [key in TimeScale]: TimeScaleDetails } = {
    [TimeScale.Weeks]: { unit: 'w', scaleUp: null, scaleUpValue: null, decimalPlaces: 0 },
    [TimeScale.Days]: {
        unit: 'd',
        scaleUp: TimeScale.Weeks,
        scaleUpValue: 7,
        decimalPlaces: 0,
    },
    [TimeScale.Hours]: { unit: 'h', scaleUp: TimeScale.Days, scaleUpValue: 24, decimalPlaces: 1 },
    [TimeScale.Minutes]: {
        unit: 'm',
        scaleUp: TimeScale.Hours,
        scaleUpValue: 60,
        decimalPlaces: 1,
    },
    [TimeScale.Seconds]: {
        unit: 's',
        scaleUp: TimeScale.Minutes,
        scaleUpValue: 60,
        decimalPlaces: 2,
    },
    [TimeScale.Milliseconds]: {
        unit: 'ms',
        scaleUp: TimeScale.Seconds,
        scaleUpValue: 1000,
        decimalPlaces: 0,
    },
    [TimeScale.Microseconds]: {
        unit: `${String.fromCharCode(181)}s`,
        scaleUp: TimeScale.Milliseconds,
        scaleUpValue: 1000,
        decimalPlaces: 0,
    },
    [TimeScale.Nanoseconds]: {
        unit: 'ns',
        scaleUp: TimeScale.Microseconds,
        scaleUpValue: 1000,
        decimalPlaces: 0,
    },
};

/**
 * Scales up the given time with the given starting scale to the scale value that should
 * not be scaled up any further.
 *
 * @param time A time duration value
 * @param startingScale The unit in which the given duration is expressed
 * @param decimalPlaces The number of decimal places desired for the resulting value
 */
export function scaleTime(
    time: number,
    startingScale: TimeScale = TimeScale.Microseconds, // eslint-disable-line default-param-last
    decimalPlaces?: number
): { value: number | string; scale: string } {
    if (!time || time === 0) {
        return {
            scale: timeScaleMap[TimeScale.Milliseconds].unit,
            value: 0,
        };
    }

    // Get initial set of scale properties
    let iScale: TimeScaleDetails = timeScaleMap[startingScale];

    let timeValue = time;
    // Keep sign
    let sign = 1;
    if (time < 0) {
        sign = -1;
        timeValue = Math.abs(time);
    }

    // Move up to the next scale if it's necessary and available
    while (iScale.scaleUpValue !== null && timeValue >= iScale.scaleUpValue && iScale.scaleUp) {
        timeValue /= iScale.scaleUpValue;
        iScale = timeScaleMap[iScale.scaleUp];
    }

    // Restore sign
    timeValue *= sign;

    return {
        scale: iScale.unit,
        value: timeValue.toFixed(typeof decimalPlaces !== 'undefined' ? decimalPlaces : iScale.decimalPlaces),
    };
}
